import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Button,
  Form,
  Alert,
  Spinner,
  Row,
  Col,
  InputGroup,
  Badge,
  Card,
} from "react-bootstrap";
import { getCasesByPatientId, createCase } from "../../acases";

/* ========== Helpers ========== */

// Normalize various _id shapes
const normId = (c) => String(c?._id?.$oid || c?._id || c?.id || "");

// Keep dates as strings "MM-DD-YYYY" (only convert around the date input)
const mmddyyyyToYyyyMmDd = (s) => {
  if (!s) return "";
  const [mm, dd, yyyy] = String(s).split("-");
  if (!mm || !dd || !yyyy) return "";
  return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
};
const yyyyMmDdToMmDdYyyy = (s) => {
  if (!s) return "";
  const [yyyy, mm, dd] = String(s).split("-");
  if (!yyyy || !mm || !dd) return "";
  return `${mm.padStart(2, "0")}-${dd.padStart(2, "0")}-${yyyy}`;
};

// For inbound values that could be ISO or {$date}
const normalizeToMMDDYYYY = (v) => {
  if (!v) return "";
  if (typeof v === "string") {
    if (/^\d{2}-\d{2}-\d{4}$/.test(v)) return v; // already MM-DD-YYYY
    const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/); // ISO YYYY-MM-DD...
    if (m) return `${m[2]}-${m[3]}-${m[1]}`;
  }
  if (v && typeof v === "object" && v.$date) {
    const iso = String(v.$date);
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[2]}-${m[3]}-${m[1]}`;
  }
  return "";
};

// Label for the existing cases dropdown
const caseOptionLabel = (c) => {
  const desc = (c?.caseDescription || "").trim() || "Untitled Case";
  const s = normalizeToMMDDYYYY(c?.caseStartDate);
  const e = normalizeToMMDDYYYY(c?.caseEndDate);
  const range = s || e ? `${s || "—"}${e ? ` → ${e}` : ""}` : "";
  return range ? `${desc} • ${range}` : desc;
};

// (invoice) icdSelections -> codes
const codeOf = (obj) => obj?.ICD10_Code || obj?.ICD10_3_Code || "";
const icdFromSelections = (selections = []) => {
  const primaries = selections.filter((s) => s.isPrimary).map((s) => s.item);
  const secondaries = selections.filter((s) => !s.isPrimary).map((s) => s.item);
  const icd101 = primaries[0] ? codeOf(primaries[0]) : "";
  const icd102 = secondaries.map(codeOf).filter(Boolean);
  return { icd101, icd102 };
};

// Lean case object back to parent (for invoice, etc.)
const mapCaseForEmit = (c) => ({
  _id: normId(c),
  caseDescription: c.caseDescription || "",
  referringDoctorName: c.referringDoctorName || "",
  referringDoctorBHF: c.referringDoctorBHF || "",
  referringDoctorHPCSA: c.referringDoctorHPCSA || "",
  authorizedBy: c.authorizedBy || "",
  authorizationNumber: c.authorizationNumber || "",
  caseStartDate: normalizeToMMDDYYYY(c.caseStartDate),
  caseEndDate: normalizeToMMDDYYYY(c.caseEndDate),
  icd101: c.icd101 || "",
  icd102: Array.isArray(c.icd102) ? c.icd102 : [],
  icd101Full: c.icd101Full || null,
 icd102Full: Array.isArray(c.icd102Full) ? c.icd102Full : [],
 icdSelections: Array.isArray(c.icdSelections) ? c.icdSelections : undefined,
});

/* ========== Component ========== */

export default function CasePickerModal({
  show,
  onHide,
  patientId,
  currentUserId,
  existingReferrers = [],     // [{id, name, bhf, hpcsa}, ...]
  invoiceICDSelections = [],  // [{item, isPrimary}, ...]
  onConfirm,                  // (pickedCaseObj) => void
}) {
  // Existing cases
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [cases, setCases] = useState([]);
  const [selectedCaseId, setSelectedCaseId] = useState("");
  const [mode, setMode] = useState("existing"); // "existing" | "new"

  // New case (inline) fields
  const [useExistingReferrer, setUseExistingReferrer] = useState(true);
  const [selectedReferrerId, setSelectedReferrerId] = useState(
    existingReferrers?.[0]?.id || ""
  );
  const [refName, setRefName] = useState("");
  const [refBHF, setRefBHF] = useState("");
  const [refHPCSA, setRefHPCSA] = useState("");
  const [caseDescription, setCaseDescription] = useState("");
  const [authorizedBy, setAuthorizedBy] = useState("");
  const [authorizationNumber, setAuthorizationNumber] = useState("");
  const [caseStartInput, setCaseStartInput] = useState(""); // YYYY-MM-DD for input
  const [caseEndInput, setCaseEndInput] = useState("");     // YYYY-MM-DD for input
  const [caseMaxSessions, setCaseMaxSessions] = useState("");

  // ICD codes (taken from invoice selections, not editing here)
  const { icd101, icd102 } = useMemo(
    () => icdFromSelections(invoiceICDSelections),
    [invoiceICDSelections]
  );

  // Selected existing case object
  const selectedCase = useMemo(
    () => cases.find((c) => normId(c) === selectedCaseId) || null,
    [cases, selectedCaseId]
  );

  // Load existing cases when opening or patient changes
  useEffect(() => {
    let cancel = false;
    (async () => {
      if (!show || !patientId) return;
      setLoading(true);
      setErr("");
      try {
        const data = await getCasesByPatientId(patientId);
        const arr = Array.isArray(data)
          ? data
          : Array.isArray(data?.cases)
          ? data.cases
          : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.items)
          ? data.items
          : [];
        // sort by start desc (best-effort)
        const sorted = [...arr].sort((a, b) => {
          const aKey = normalizeToMMDDYYYY(a?.caseStartDate);
          const bKey = normalizeToMMDDYYYY(b?.caseStartDate);
          const aS = aKey ? `${aKey.slice(6, 10)}${aKey.slice(0, 2)}${aKey.slice(3, 5)}` : "";
          const bS = bKey ? `${bKey.slice(6, 10)}${bKey.slice(0, 2)}${bKey.slice(3, 5)}` : "";
          return bS.localeCompare(aS);
        });
        if (!cancel) {
          setCases(sorted);
          if (sorted[0]) {
            setSelectedCaseId(normId(sorted[0]));
            setMode("existing");
          } else {
            setSelectedCaseId("");
            setMode("new");
          }
        }
      } catch (e) {
        if (!cancel) {
          setErr("Failed to load cases.");
          setCases([]);
          setSelectedCaseId("");
          setMode("new");
        }
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [show, patientId]);

  // Keep referrer fields in sync when using an existing one
  useEffect(() => {
    if (!useExistingReferrer) return;
    const r = existingReferrers.find((x) => x.id === selectedReferrerId);
    if (r) {
      setRefName(r.name || "");
      setRefBHF(r.bhf || "");
      setRefHPCSA(r.hpcsa || "");
    } else {
      setRefName("");
      setRefBHF("");
      setRefHPCSA("");
    }
  }, [useExistingReferrer, selectedReferrerId, existingReferrers]);

  // Reset new-case form when modal opens or when switching to "new"
  useEffect(() => {
    if (!show) return;
    if (mode !== "new") return;
    setErr("");
    setCaseDescription("");
    setAuthorizedBy("");
    setAuthorizationNumber("");
    setCaseStartInput("");
    setCaseEndInput("");
    setCaseMaxSessions("");
    setUseExistingReferrer(true);
    setSelectedReferrerId(existingReferrers?.[0]?.id || "");
  }, [show, mode, existingReferrers]);

  // Emit a picked existing case
  const useSelectedCase = () => {
    setErr("");
    if (!selectedCaseId) {
      setErr("Please select a case or switch to Create New.");
      return;
    }
    const found = cases.find((c) => normId(c) === selectedCaseId);
    if (!found) {
      setErr("Selected case not found.");
      return;
    }
    onConfirm?.(mapCaseForEmit(found));
    onHide?.();
  };

  // Create a new case (saved to DB) then use it
  const createAndUseNewCase = async () => {
    setErr("");
    if (!patientId) {
      setErr("Patient is required.");
      return;
    }
    if (!icd101) {
      setErr("New case requires at least one primary ICD-10 (taken from the invoice).");
      return;
    }

    const payload = {
      patientId,
      referringDoctorName: refName || "",
      referringDoctorBHF: refBHF || "",
      referringDoctorHPCSA: refHPCSA || "",
      icd101,
      icd102,
      icdSelections: invoiceICDSelections, // keep full shape if you store it
      caseDescription: caseDescription || "",
      authorizedBy: authorizedBy || "",
      authorizationNumber: authorizationNumber || "",
      caseStartDate: yyyyMmDdToMmDdYyyy(caseStartInput) || "",
      caseEndDate: yyyyMmDdToMmDdYyyy(caseEndInput) || "",
      caseMaxSessions: caseMaxSessions || "",
      caseCreatedBy: currentUserId || "",
      caseUpdatedBy: currentUserId || "",
    };

    try {
      const created = await createCase(payload);
     // Merge so we keep ALL the fields we just submitted, but also carry back any IDs the server set.
     const hydrated = { ...payload, ...created };
     // Ensure we keep the server id if it came back as _id/insertedId/etc.
     const serverId =
       created?._id?.$oid || created?._id || created?.id || created?.insertedId;
     if (serverId) hydrated._id = serverId;
     const emitted = mapCaseForEmit(hydrated);
      onConfirm?.(emitted);
      onHide?.();
    } catch (e) {
      setErr(e?.message || "Failed to create case.");
    }
  };

  const hasExisting = cases.length > 0;

  // Pretty preview helpers
  const PreviewRow = ({ label, value }) => (
    <Row className="mb-1">
      <Col xs={5} md={4} className="text-muted small">
        {label}
      </Col>
      <Col xs={7} md={8}>
        {value || "—"}
      </Col>
    </Row>
  );

  return (
    <Modal show={!!show} onHide={onHide} centered size="lg" scrollable>
      <Modal.Header
              closeButton
              style={{ background: "#CFE2FF" }}
              className="rounded-0 text-primary d-flex align-items-center"
            >
              <Modal.Title as="h6" className="m-0">
                Attach Case
              </Modal.Title>
            </Modal.Header>

      <Modal.Body>
        {!patientId && (
          <Alert variant="warning" className="mb-3">
            Please select a patient first.
          </Alert>
        )}

        {/* Existing cases chooser + mode toggle */}
        <div className="d-flex gap-2 align-items-end mb-3">
          <div className="flex-grow-1">
            <Form.Label className="fw-bold mb-1">Existing Cases</Form.Label>
            <Form.Select
              size="sm"
              value={selectedCaseId}
              onChange={(e) => {
                setSelectedCaseId(e.target.value);
                setMode("existing");
              }}
              disabled={!patientId || loading || !hasExisting}
              className="rounded-0"
            >
              {loading ? (
                <option value="">Loading…</option>
              ) : !hasExisting ? (
                <option value="">No existing cases for this patient</option>
              ) : (
                <>
                  <option value="">Choose a case…</option>
                  {cases.map((c) => {
                    const id = normId(c);
                    return (
                      <option key={id} value={id}>
                        {caseOptionLabel(c)}
                      </option>
                    );
                  })}
                </>
              )}
            </Form.Select>
            {loading && <Spinner animation="border" size="sm" className="mt-2" />}
          </div>

          <div className="pb-1">
            {mode === "existing" ? (
              <Button
                variant="outline-primary"
                size="sm"
                className="rounded-0"
                onClick={() => {
                  setSelectedCaseId("");
                  setMode("new");
                }}
              >
                + Create New Case
              </Button>
            ) : hasExisting ? (
              <Button
                variant="outline-secondary"
                size="sm"
                className="rounded-0"
                onClick={() => {
                  if (cases[0]) setSelectedCaseId(normId(cases[0]));
                  setMode("existing");
                }}
              >
                ← Back to Existing
              </Button>
            ) : null}
          </div>
        </div>

        {/* Existing case preview */}
        {mode === "existing" && selectedCaseId && selectedCase && (
          <Card className="mb-3">
            <Card.Body>
              <h6 className="text-primary mb-2">
                {selectedCase.caseDescription?.trim() || "Untitled Case"}
              </h6>

              <Row>
                <Col md={7}>
                  <PreviewRow
                    label="Referring Doctor"
                    value={selectedCase.referringDoctorName}
                  />
                  <PreviewRow
                    label="BHF / HPCSA"
                    value={
                      (selectedCase.referringDoctorBHF || "—") +
                      " / " +
                      (selectedCase.referringDoctorHPCSA || "—")
                    }
                  />
                  <PreviewRow
                    label="Authorized By"
                    value={selectedCase.authorizedBy}
                  />
                  <PreviewRow
                    label="Authorization #"
                    value={selectedCase.authorizationNumber}
                  />
                </Col>
                <Col md={5}>
                  <PreviewRow
                    label="Start Date"
                    value={normalizeToMMDDYYYY(selectedCase.caseStartDate)}
                  />
                  <PreviewRow
                    label="End Date"
                    value={normalizeToMMDDYYYY(selectedCase.caseEndDate)}
                  />
                  <PreviewRow
                    label="Max Sessions"
                    value={selectedCase.caseMaxSessions}
                  />
                  <div className="mt-2">
                    <div className="small text-muted mb-1">ICD-10</div>
                    {selectedCase.icd101 && (
                      <Badge bg="secondary" className="me-1">
                        {selectedCase.icd101}
                      </Badge>
                    )}
                    {Array.isArray(selectedCase.icd102) &&
                      selectedCase.icd102.map((code, i) => (
                        <Badge key={i} bg="light" text="dark" className="me-1">
                          {code}
                        </Badge>
                      ))}
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        {/* New case form */}
        {mode === "new" && (
          <div className="border rounded p-2">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong>Create New Case</strong>
              <Form.Check
                type="switch"
                id="use-existing-ref-switch"
                label={useExistingReferrer ? "Using existing referrer" : "Create new referrer"}
                checked={useExistingReferrer}
                onChange={(e) => setUseExistingReferrer(e.target.checked)}
              />
            </div>

            {useExistingReferrer ? (
              <>
                <Form.Group className="mb-2">
                  <Form.Label>Referring Doctor</Form.Label>
                  <Form.Select
                    size="sm"
                    value={selectedReferrerId}
                    onChange={(e) => setSelectedReferrerId(e.target.value)}
                  >
                    {existingReferrers.length === 0 && (
                      <option value="">No referrers found</option>
                    )}
                    {existingReferrers.map((r) => (
                      <option key={r.id || r.hpcsa || r.bhf} value={r.id}>
                        {r.name || "-"} {r.bhf ? `| BHF ${r.bhf}` : ""}{" "}
                        {r.hpcsa ? `| HPCSA ${r.hpcsa}` : ""}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Row className="mb-2">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>BHF</Form.Label>
                      <Form.Control size="sm" value={refBHF} readOnly />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>HPCSA</Form.Label>
                      <Form.Control size="sm" value={refHPCSA} readOnly />
                    </Form.Group>
                  </Col>
                </Row>
              </>
            ) : (
              <Row className="mb-2">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Referrer Name</Form.Label>
                    <Form.Control
                      size="sm"
                      value={refName}
                      onChange={(e) => setRefName(e.target.value)}
                      placeholder="Dr Jane Doe"
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>BHF</Form.Label>
                    <Form.Control
                      size="sm"
                      value={refBHF}
                      onChange={(e) => setRefBHF(e.target.value)}
                      placeholder="e.g. 0123456"
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>HPCSA</Form.Label>
                    <Form.Control
                      size="sm"
                      value={refHPCSA}
                      onChange={(e) => setRefHPCSA(e.target.value)}
                      placeholder="e.g. MP0123456"
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}

            <Form.Group className="mb-2">
              <Form.Label>Case Description</Form.Label>
              <Form.Control
                size="sm"
                value={caseDescription}
                onChange={(e) => setCaseDescription(e.target.value)}
                placeholder="Brief description of the case"
              />
            </Form.Group>

            <Row className="mb-2">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Authorized By</Form.Label>
                  <Form.Control
                    size="sm"
                    value={authorizedBy}
                    onChange={(e) => setAuthorizedBy(e.target.value)}
                    placeholder="Name of authorizer"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Authorization Number</Form.Label>
                  <Form.Control
                    size="sm"
                    value={authorizationNumber}
                    onChange={(e) => setAuthorizationNumber(e.target.value)}
                    placeholder="Auth number"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-2">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control
                    size="sm"
                    type="date"
                    value={caseStartInput}
                    onChange={(e) => setCaseStartInput(e.target.value)}
                  />
                  <div className="form-text">
                    Will be saved as <code>{yyyyMmDdToMmDdYyyy(caseStartInput) || "MM-DD-YYYY"}</code>
                  </div>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>End Date</Form.Label>
                  <Form.Control
                    size="sm"
                    type="date"
                    value={caseEndInput}
                    onChange={(e) => setCaseEndInput(e.target.value)}
                  />
                  <div className="form-text">
                    Will be saved as <code>{yyyyMmDdToMmDdYyyy(caseEndInput) || "MM-DD-YYYY"}</code>
                  </div>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Max Sessions</Form.Label>
                  <InputGroup>
                    <Form.Control
                      size="sm"
                      type="number"
                      min={0}
                      step={1}
                      value={caseMaxSessions}
                      onChange={(e) => setCaseMaxSessions(e.target.value)}
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>

            {/* ICD preview taken from invoice */}
            <div className="mt-2">
              <div className="small text-muted mb-1">ICD-10 (from invoice)</div>
              {icd101 ? (
                <div className="small">
                  <strong>Primary:</strong> {icd101}
                  {icd102?.length ? (
                    <>
                      {"  "} <strong>Secondary:</strong> {icd102.join(", ")}
                    </>
                  ) : null}
                </div>
              ) : (
                <div className="small text-danger">
                  No primary ICD-10 selected on the invoice — required to create a case.
                </div>
              )}
            </div>
          </div>
        )}

        {err && <div className="text-danger mt-3">{err}</div>}
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex w-100 justify-content-between">
          <div />
          <div className="d-flex gap-2">
            {mode === "existing" && selectedCaseId && (
              <Button
                variant="primary"
                className="rounded-0"
                onClick={useSelectedCase}
                disabled={!patientId || loading}
              >
                Use Selected Case
              </Button>
            )}
            {mode === "new" && (
              <Button
                variant="primary"
                className="rounded-0"
                onClick={createAndUseNewCase}
                disabled={!patientId || loading}
              >
                Create & Use New Case
              </Button>
            )}
            <Button variant="secondary" className="rounded-0" onClick={onHide}>
              Cancel
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
